import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { changeLocale, currentLocale } from './index';
import { LOCALES, LOCALE_NAMES, type LocaleCode, isLocale } from './locales';

/**
 * The language picker, in both shells (BP-19 §6).
 *
 * A native `<select>` and not a menu: it is three lines of markup, it is the control
 * every phone already knows how to render full-screen, and it needs no focus trap to
 * be usable with a keyboard. The options are written in their own language
 * (`LOCALE_NAMES`), because a reader looking for Gujarati is looking for "ગુજરાતી".
 *
 * **The choice is remembered in two places and they are not the same place.**
 * `localStorage` is this browser's, is written on every change and is what makes a
 * reload keep the language even when nobody is signed in — the booking journey is
 * anonymous until the OTP step (rule 6). `persist` is the account's, is supplied by
 * the shell and writes `PUT /me/locale`; the client shell leaves it out while there
 * is no session. Neither is the source of truth on its own: the shell resolves the
 * chain at startup and `resolveLocale` decides.
 */

/** This browser's remembered choice. Namespaced, because an origin is shared. */
export const LOCALE_STORAGE_KEY = '26ad.locale';

/** The remembered locale, or `null` when there is none or storage refuses. */
export function storedLocale(): LocaleCode | null {
  try {
    const value = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    return isLocale(value) ? value : null;
  } catch {
    // Safari in private mode throws on access rather than returning null. A
    // forgotten preference is a smaller problem than a shell that will not mount.
    return null;
  }
}

/** Remember `locale` for this browser. Silent when storage refuses, for the same reason. */
export function rememberLocale(locale: LocaleCode): void {
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    return;
  }
}

/** Switch the running instance and remember the choice. */
export function applyLocale(locale: LocaleCode): void {
  changeLocale(locale);
  rememberLocale(locale);
}

export type LocaleSwitcherProps = {
  /** Write the choice to the account as well. Omitted while nobody is signed in. */
  readonly persist?: ((locale: LocaleCode) => void) | undefined;
};

export function LocaleSwitcher({ persist }: LocaleSwitcherProps): JSX.Element {
  const { t } = useTranslation('common');
  const active = currentLocale();

  function onPick(value: string): void {
    if (!isLocale(value) || value === active) {
      return;
    }
    applyLocale(value);
    persist?.(value);
  }

  return (
    <label className="flex items-center gap-2 text-sm" data-testid="locale-switcher">
      <span className="sr-only">{t('locale.label')}</span>
      <select
        aria-label={t('locale.switch')}
        title={t('locale.draft')}
        value={active}
        onChange={(event) => {
          onPick(event.target.value);
        }}
        className="rounded border border-border bg-card px-1 py-0.5"
      >
        {LOCALES.map((locale) => (
          <option key={locale} value={locale}>
            {LOCALE_NAMES[locale]}
          </option>
        ))}
      </select>
    </label>
  );
}
